/**
 * @file Plays the short reel clips in the Instagram row (sections/instagram-look.liquid) the way a
 * social feed does: on their own, without sound, and only while the tile is on the screen.
 *
 * Instagram's own embed never plays without three taps, which is what the owner asked to be rid of.
 * The clips are muted and looping, so autoplay is allowed on iOS and Android, and each one is only
 * fetched when its tile comes near the screen: nothing is downloaded for a visitor who never
 * scrolls that far. A tile that is scrolled away pauses, so a phone never decodes six videos at
 * once.
 *
 * It stops before starting at all when the visitor asked for less motion or less data: the poster
 * picture is then the whole tile, which is what the section rendered before any of this.
 */
const CLIP = '[data-look-clip]';
/** Start fetching a clip while its tile is still this far off the screen. */
const NEAR = '200px';

class BkLookClips extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #watcher = null;

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const connection = /** @type {{ saveData?: boolean, effectiveType?: string }} */ (
      /** @type {any} */ (navigator).connection ?? {}
    );
    if (connection.saveData || /2g/.test(connection.effectiveType ?? '')) return;

    const clips = /** @type {HTMLVideoElement[]} */ ([...this.querySelectorAll(CLIP)]);
    if (clips.length === 0) return;

    this.#watcher = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const clip = /** @type {HTMLVideoElement} */ (entry.target);
          if (entry.isIntersecting) this.#play(clip);
          else this.#pause(clip);
        }
      },
      { rootMargin: NEAR, threshold: 0.2 },
    );
    for (const clip of clips) {
      // Muted is set as an attribute for the markup to be honest, and as a property because Safari
      // only trusts the property when it decides whether autoplay is allowed.
      clip.muted = true;
      clip.addEventListener('playing', () => clip.setAttribute('data-playing', ''));
      this.#watcher.observe(clip);
    }

    // A tab in the background must not keep six videos decoding.
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    this.#watcher?.disconnect();
    this.#watcher = null;
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #onVisibility = () => {
    if (!document.hidden) return;
    for (const clip of this.querySelectorAll(CLIP)) this.#pause(/** @type {HTMLVideoElement} */ (clip));
  };

  /** @param {HTMLVideoElement} clip */
  #play(clip) {
    // The HTML carries no address, so nothing is fetched before this moment in any browser.
    if (!clip.getAttribute('src') && clip.dataset.src) {
      clip.preload = 'auto';
      clip.src = clip.dataset.src;
    }
    const started = clip.play();
    // Autoplay can still be refused (a battery saver, a browser setting): the poster stays, which is
    // exactly what the tile looked like before.
    if (started && typeof started.catch === 'function') started.catch(() => {});
  }

  /** @param {HTMLVideoElement} clip */
  #pause(clip) {
    if (!clip.paused) clip.pause();
  }
}

if (!customElements.get('bk-look-clips')) customElements.define('bk-look-clips', BkLookClips);
